import { useState, useEffect, useCallback, useRef } from 'react';
import styles from './ToastStack.module.css';

export type ToastType = 'info' | 'success' | 'warning' | 'error';

interface ToastItem {
  id: number;
  msg: string;
  type: ToastType;
  expiredAt: number; // performance.now() + duration
}

let _addToast: ((msg: string, type?: ToastType) => void) | null = null;
let _suppressSuccessToast = false;

/** 开启/关闭成功类绿色弹窗的抑制（当有全局弹窗时调用） */
export function setSuppressSuccessToast(v: boolean) {
  _suppressSuccessToast = v;
}

export function addToast(msg: string, type: ToastType = 'info') {
  // 当有全局弹窗时，跳过绿色成功提示（避免双重弹窗干扰）
  if (_suppressSuccessToast && type === 'success') return;
  _addToast?.(msg, type);
}

export default function ToastStack() {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const timerRef = useRef<number | null>(null);

  const removeToast = useCallback((id: number) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  const add = useCallback((msg: string, type: ToastType = 'info') => {
    const id = Date.now() + Math.random();
    const duration = 2600;
    const item: ToastItem = { id, msg, type, expiredAt: performance.now() + duration };
    setToasts(prev => {
      const next = [...prev, item];
      return next.length > 4 ? next.slice(-4) : next;
    });
    setTimeout(() => removeToast(id), duration);
  }, [removeToast]);

  // 暴露 addToast
  useEffect(() => {
    _addToast = add;
    return () => { _addToast = null; };
  }, [add]);

  // 清理定时器
  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  if (toasts.length === 0) return null;

  return (
    <div className={styles.container}>
      {toasts.map(t => (
        <div
          key={t.id}
          className={`${styles.toast} ${styles[t.type]}`}
          onClick={() => removeToast(t.id)}
        >
          <span className={styles.icon}>
            {t.type === 'info' && 'ℹ️'}
            {t.type === 'success' && '✅'}
            {t.type === 'warning' && '⚠️'}
            {t.type === 'error' && '❌'}
          </span>
          <span className={styles.msg}>{t.msg}</span>
        </div>
      ))}
    </div>
  );
}
